/**
 * AfterService digital assistant - Main App Component
 * Supporting Australian Veterans with AI-powered guidance
 * Features: DVA policy assistance, claims support, veteran-focused interface
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  CssBaseline,
  ThemeProvider,
  createTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Typography,
  Button,
} from "@mui/material";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import AppLayout from "./components/AppLayout";
import { Authenticator } from "@aws-amplify/ui-react";
import { Amplify } from "aws-amplify";
import { getCurrentUser } from "aws-amplify/auth";
import { getCognitoUserWithAttributes } from "./utils/cognitoHelper";

// Import custom password reset component
import PasswordResetFlow from "./components/auth/PasswordResetFlow";
// Phase 999.72 Plan 02 (D-05): gate AI routes behind onboarding + consent
import AIConsentGate from "./components/auth/AIConsentGate";
// Phase 999.72 Plan 11 (D-33 / D-34): reconsent modal for T&C v2.0 bump
import ReconsentPrompt, { reconsentTextFor } from "./components/onboarding/ReconsentPrompt";
import { CURRENT_CONSENT_VERSION } from "./config/consentVersion";
// Phase 999.75 Plan 02 (D-05, D-23): cross-cutting widget invocation API
import { AdvocateWidgetProvider } from "./contexts/AdvocateWidgetContext";
import { useNoticeDeferred } from "./components/noticeDeferral";
// F-11 (dev review #34): reset scroll position on route change
import ScrollToTop from "./components/common/ScrollToTop";

import appConfig, { getAmplifyConfiguration } from "./config/config";
import { formFields } from "./config/amplifyConfig";
import {
  useConfig,
  useThemeConfig,
  useFeatureFlags,
} from "./components/ConfigProvider";
import apiService from "./services/apiService";
import ErrorBoundary from "./components/ErrorBoundary";
// Drop 2 (C-01, C-02): numbers-only crisis strip on the sign-in, sign-up,
// reset and loading screens (placement signed by Alex 5 Oct 2026).
import PublicCrisisStrip from "./components/safety/PublicCrisisStrip";
import PublicRouter from "./components/PublicRouter";
import LoadingFallback from "./components/LoadingFallback";

// Beta notice dismissal is persisted per notice version. Bump the version
// suffix to re-show the modal to every user when the beta notice content
// materially changes.
const BETA_NOTICE_VERSION = "v1";
const BETA_NOTICE_DISMISSED_KEY = `afterservice_beta_notice_dismissed_${BETA_NOTICE_VERSION}`;

function WidgetGuard({ children }) {
  const { pathname } = useLocation();
  if (pathname === '/assistant') return null;
  return children;
}

// Lazy load components for better performance
const UserProfile = React.lazy(() => import("./pages/UserProfile"));
const EarlyAccessAlert = React.lazy(
  () => import("./components/EarlyAccessAlert"),
);
const FeedbackAlert = React.lazy(() => import("./components/FeedbackAlert"));

// The beta notice and the feedback dialog must never sit over a crisis
// surface (P0-2, 4 Oct 2026; C-09, 5 Oct 2026). See components/noticeDeferral.
function BetaNotice({ open, onClose, onOpenFeedback }) {
  const deferred = useNoticeDeferred();
  return (
    <EarlyAccessAlert
      open={open && !deferred}
      onClose={onClose}
      onOpenFeedback={onOpenFeedback}
    />
  );
}

function FeedbackNotice({ open, onClose, onNotNow }) {
  const deferred = useNoticeDeferred();
  return (
    <FeedbackAlert open={open && !deferred} onClose={onClose} onNotNow={onNotNow} />
  );
}
const OnboardingFlow = React.lazy(
  () => import("./components/onboarding/OnboardingFlow"),
);
const CrisisPage = React.lazy(() => import("./pages/CrisisPage"));
const ToolsCenter = React.lazy(() => import("./components/tools/ToolsCenter"));
const AdvocateChatEmbed = React.lazy(() => import("./pages/AdvocateChatEmbed"));
// Phase 999.100 W4 (D-16 + W4-D-07): /about-ai is public + unauthenticated.
// Mounted via the early-exit block below (same pattern as /embed) so the
// page renders without the Authenticator wrapper. Drop 2: the public router
// (with public /crisis and a full-reload catch-all) lives in
// components/PublicRouter.js, imported at the top of this file.
// Phase 999.100 W4 (W4-D-08): /legal/privacy stub removed 2026-05-10 per
// cofounder decision to consolidate to a single canonical privacy URL on the
// marketing site (https://afterservice.ai/privacy). The in-app stub is gone;
// AppFooter + auth screens link to the marketing-site canonical directly.
const ServiceDirectory = React.lazy(() => import("./pages/ServiceDirectory"));
const EligibilityHolding = React.lazy(() => import("./pages/EligibilityHolding"));
const InsightsDashboard = React.lazy(() => import("./pages/InsightsDashboard"));
const EntitlementsPage = React.lazy(() => import("./pages/EntitlementsPage"));
const ClaimsPage = React.lazy(() => import("./pages/ClaimsPage"));
const MentalHealthPage = React.lazy(() => import("./pages/MentalHealthPage"));
const AppealsPage = React.lazy(() => import("./pages/AppealsPage"));
const ProviderRegistration = React.lazy(() => import("./components/provider/ProviderRegistration"));
const ProviderDirectory = React.lazy(() => import("./components/provider/ProviderDirectory"));
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const DocumentVaultPage = React.lazy(() => import("./pages/DocumentVaultPage"));
const NotificationsPage = React.lazy(() => import("./pages/NotificationsPage"));
const EventsCalendarPage = React.lazy(() => import("./pages/EventsCalendarPage"));
const TransitionTimelinePage = React.lazy(() => import("./pages/TransitionTimelinePage"));
const AdvocateChatPage = React.lazy(() => import("./pages/AdvocateChatPage"));
const HelpPage = React.lazy(() => import("./pages/HelpPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const AIChatWidget = React.lazy(() => import("./components/AIChatWidget"));
const NotFoundPage = React.lazy(() => import("./pages/NotFoundPage"));
const FindSupportPage = React.lazy(() => import("./pages/FindSupportPage"));
const MyOrgsPage = React.lazy(() => import("./pages/MyOrgsPage"));

// Development testing utilities
if (process.env.NODE_ENV === "development") {
  import("./utils/localhostTest")
    .then(() => {
      console.log(
        "🔬 [LOCALHOST] Testing utilities loaded - check browser console for commands",
      );
    })
    .catch((error) => {
      console.warn(
        "⚠️ [LOCALHOST] Could not load testing utilities:",
        error.message,
      );
    });

  // Load WebSocket authentication test utilities
  import("./test/websocket-auth-test")
    .then(() => {
      console.log("🔌 [WEBSOCKET] Authentication test utilities loaded");
    })
    .catch((error) => {
      console.warn(
        "⚠️ [WEBSOCKET] Could not load test utilities:",
        error.message,
      );
    });

  // Add WebSocket debugging utilities
  import("./services/simpleWebSocketChat")
    .then((module) => {
      const simpleWebSocketChat = module.default;

      // Global debugging functions
      window.debugWebSocket = () => {
        const status = simpleWebSocketChat.getConnectionStatus();
        console.log("🔍 WebSocket Connection Status:", status);
        return status;
      };

      window.forceWebSocketCleanup = () => {
        simpleWebSocketChat.forceCleanup();
        console.log("✅ WebSocket force cleanup completed");
      };

      window.testConnectionStability = async () => {
        console.log("🧪 Testing WebSocket connection stability...");

        for (let i = 0; i < 5; i++) {
          console.log(`Test ${i + 1}: Checking connection...`);
          const status = simpleWebSocketChat.getConnectionStatus();
          console.log(`  - Connected: ${status.isConnected}`);
          console.log(`  - Handlers: ${status.eventHandlers.length}`);
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }

        console.log("✅ Connection stability test complete");
        return simpleWebSocketChat.getConnectionStatus();
      };

      console.log("🔧 [WEBSOCKET] Debugging utilities loaded");
    })
    .catch((error) => {
      console.warn(
        "⚠️ [WEBSOCKET] Could not load debugging utilities:",
        error.message,
      );
    });

  // Add alert testing utilities
  window.resetEarlyAccessAlert = () => {
    sessionStorage.removeItem("afterservice_early_access_alert_shown");
    sessionStorage.removeItem("afterservice_early_access_alert_timestamp");
    console.log(
      "🔔 Early Access Alert reset - will show on next app ready state",
    );

    // Force reload the alert logic if we're currently in the app
    if (window.APP_DEBUG?.appReady && window.APP_DEBUG?.user) {
      console.log("🔔 App is ready, triggering immediate alert check...");
      window.dispatchEvent(new CustomEvent("force-show-early-access"));
    }
  };

  window.resetFeedbackAlert = () => {
    localStorage.removeItem("afterservice_feedback_alert_dismissed");
    console.log("🔔 Feedback Alert reset - timer will restart");
  };

  window.showEarlyAccessAlert = () => {
    window.dispatchEvent(new CustomEvent("force-show-early-access"));
    console.log("🔔 Forcing Early Access Alert to show");
  };

  window.checkEarlyAccessAlert = () => {
    const hasSeenAlert = sessionStorage.getItem(
      "afterservice_early_access_alert_shown",
    );
    const alertTimestamp = sessionStorage.getItem(
      "afterservice_early_access_alert_timestamp",
    );
    console.log("🔔 Early Access Alert session storage:", {
      hasSeenAlert: !!hasSeenAlert,
      alertTimestamp,
      timestampDate: alertTimestamp
        ? new Date(parseInt(alertTimestamp)).toISOString()
        : "N/A",
    });
    console.log("🔔 Current alert state variables:", {
      appReady: window.APP_DEBUG?.appReady,
      hasUser: !!window.APP_DEBUG?.user,
      showEarlyAccessAlert: window.APP_DEBUG?.showEarlyAccessAlert,
    });
    return !hasSeenAlert;
  };

  window.forceShowEarlyAccessAlert = () => {
    console.log("🔔 Forcing Early Access Alert to show immediately");
    window.dispatchEvent(new CustomEvent("force-show-early-access"));
    return true;
  };

  window.debugEarlyAccessAlert = () => {
    const hasSeenAlert = sessionStorage.getItem(
      "afterservice_early_access_alert_shown",
    );
    const alertTimestamp = sessionStorage.getItem(
      "afterservice_early_access_alert_timestamp",
    );
    const debug = window.APP_DEBUG || {};

    console.log("🔔 === EARLY ACCESS ALERT DEBUG REPORT ===");
    console.log("🔔 Session Storage:", {
      hasSeenAlert: !!hasSeenAlert,
      alertTimestamp,
      timestampDate: alertTimestamp
        ? new Date(parseInt(alertTimestamp)).toISOString()
        : "N/A",
    });
    console.log("🔔 App State:", debug);
    console.log(
      "🔔 Should Show Alert:",
      !hasSeenAlert && debug.appReady && debug.user,
    );
    console.log("🔔 === END DEBUG REPORT ===");

    return {
      hasSeenAlert: !!hasSeenAlert,
      alertTimestamp,
      appState: debug,
      shouldShow: !hasSeenAlert && debug.appReady && debug.user,
    };
  };

  console.log("🔔 Alert testing utilities available:");
  console.log(
    "   - resetEarlyAccessAlert() - Reset early access alert & trigger if ready",
  );
  console.log("   - resetFeedbackAlert() - Reset feedback alert timer");
  console.log("   - showEarlyAccessAlert() - Force show early access alert");
  console.log("   - forceShowEarlyAccessAlert() - Force show immediately");
  console.log("   - checkEarlyAccessAlert() - Check if alert should show");
  console.log("   - debugEarlyAccessAlert() - Complete debug report");
  console.log("🔌 WebSocket testing utilities:");
  console.log("   - testWebSocketAuth() - Test token retrieval and format");
  console.log("   - testWebSocketConnection() - Test full connection flow");
  console.log("   - runAllWebSocketTests() - Run complete test suite");
  console.log("🔧 WebSocket debugging utilities:");
  console.log(
    "   - window.debugWebSocket() - Show WebSocket connection status",
  );
  console.log(
    "   - window.forceWebSocketCleanup() - Force cleanup all connections",
  );
  console.log(
    "   - window.testConnectionStability() - Test connection stability",
  );

  // Only reset for fresh deployment if explicitly called
  window.resetForDeployment = () => {
    sessionStorage.removeItem("afterservice_early_access_alert_shown");
    sessionStorage.removeItem("afterservice_early_access_alert_timestamp");
    localStorage.removeItem("afterservice_feedback_alert_dismissed");
    console.log("🔔 All alerts reset for fresh deployment testing");
  };

  console.log("   - resetForDeployment() - Reset all alerts for fresh testing");
  console.log("   - window.APP_DEBUG - Inspect current state variables");

  // For immediate debugging of the current issue
  console.log("🔔 === IMMEDIATE DEBUG ===");
  console.log("🔔 Current session storage:");
  console.log(
    "   - afterservice_early_access_alert_shown:",
    sessionStorage.getItem("afterservice_early_access_alert_shown"),
  );
  console.log(
    "   - afterservice_early_access_alert_timestamp:",
    sessionStorage.getItem("afterservice_early_access_alert_timestamp"),
  );
  console.log(
    "🔔 TO FIX CURRENT ISSUE: Run resetEarlyAccessAlert() in console",
  );
  console.log("🔔 === END IMMEDIATE DEBUG ===");
}

// Configure Amplify with simplified configuration
const configureAmplify = () => {
  try {
    const config = getAmplifyConfiguration();
    Amplify.configure(config);
  } catch (error) {
    console.error("❌ Failed to configure Amplify:", error);
    throw error;
  }
};

// Initialize Amplify configuration immediately (synchronous)
try {
  configureAmplify();
} catch (error) {
  console.error("❌ Critical: Amplify configuration failed completely:", error);
}

// Add global styles for blue login background
const globalStyles = `
  html, body {
    font-size: 16px !important;
    overflow-x: hidden;
    width: 100% !important;
    height: 100% !important;
  }

  #root {
    width: 100% !important;
    min-height: 100% !important;
  }


  /* v1.1: the authenticator sits on the ink page - no brand-blue fill. */
  .amplify-authenticator {
    background-color: #0E1A22 !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    overflow: auto !important;
  }
  .amplify-tabs {
    background-color: transparent !important;
  }
  .amplify-authenticator__modal {
    background-color: #0E1A22 !important;
  }
  .amplify-card {
    background-color: rgba(255, 255, 255, 0.06) !important;
    backdrop-filter: blur(10px);
  }
  body .amplify-authenticator {
    background: #0E1A22 !important;
  }
  
  /* Authenticator Tab Styling */
  .amplify-tabs__list {
    display: flex !important;
    background: transparent !important;
    border-bottom: none !important;
    margin-bottom: 0 !important;
    padding: 0 !important;
    gap: 2px !important;
  }
  
  .amplify-tabs__item {
    flex: 1 !important;
    background: #f3f4f6 !important;
    color: #555 !important;
    border: none !important;
    border-radius: 12px 12px 0 0 !important;
    padding: 16px 24px !important;
    font-weight: 500 !important;
    font-size: 16px !important;
    margin: 0 !important;
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    border-bottom: 3px solid transparent !important;
    position: relative !important;
    z-index: 2 !important;
  }
  
  .amplify-tabs__item--active,
  .amplify-tabs__item[aria-selected="true"] {
    background: white !important;
    color: #1a1a1a !important;
    font-weight: 600 !important;
    border-bottom: 3px solid transparent !important;
    border-top: 3px solid #5086ed !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    z-index: 3 !important;
  }
  
  .amplify-tabs__item:hover:not(.amplify-tabs__item--active):not([aria-selected="true"]) {
    background: #e5e7eb !important;
    color: #374151 !important;
  }
  
  /* Ensure tab content area connects seamlessly with active tab */
  .amplify-tabs__panel {
    background: white !important;
    border-radius: 0 0 12px 12px !important;
    padding: 24px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    margin-top: -1px !important;
    position: relative !important;
    z-index: 1 !important;
  }
  
  /* Remove any gaps between tabs and panel */
  .amplify-tabs {
    margin-bottom: 0 !important;
  }
  
  /* Footer positioning fix - only apply when not on auth screens */
  .app-main-content .MuiBox-root.css-1m2h7ts {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    z-index: 1000 !important;
    background: #5086ed !important;
    padding: 12px 16px !important;
    text-align: center !important;
    border: none !important;
    border-top: none !important;
    box-shadow: none !important;
  }
  
  /* Footer text color fix */
  .app-main-content .MuiBox-root.css-1m2h7ts .MuiTypography-root {
    color: white !important;
  }
  
  /* Remove all fixed positioning from auth screens */
  .amplify-authenticator .MuiBox-root {
    position: static !important;
  }
  
  /* Hide the problematic copyright banner on auth screens */
  .amplify-authenticator .MuiBox-root.css-1m2h7ts {
    display: none !important;
  }
  
  /* Add copyright to the bottom of the auth area */
  .amplify-authenticator::after {
    content: "Copyright © 2026 AfterService.ai - All Rights Reserved";
    display: block;
    text-align: center;
    padding: 16px;
    color: rgba(231, 236, 241, 0.55);
    font-size: 0.75rem;
    background: transparent;
    margin-top: 24px;
  }
`;

// Inject global styles
if (typeof document !== "undefined") {
  const existingStyle = document.getElementById("amplify-blue-bg");
  if (!existingStyle) {
    const style = document.createElement("style");
    style.id = "amplify-blue-bg";
    style.textContent = globalStyles;
    document.head.appendChild(style);
  }
}

// Import password requirements component
const PasswordRequirements = React.lazy(
  () => import("./components/PasswordRequirements"),
);
const AmplifyPasswordEnhancer = React.lazy(
  () => import("./components/AmplifyPasswordEnhancer"),
);

// Custom component for legal links that gets injected into forms
const LegalLinksComponent = () => (
  <Box
    sx={{
      mt: 2,
      mb: 2,
      textAlign: "center",
      padding: "12px",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      borderRadius: "8px",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    }}
  >
    <Typography
      variant="body2"
      sx={{
        color: "#5086ed",
        mb: 1,
        fontSize: "0.85rem",
        fontWeight: 500,
      }}
    >
      By continuing, you agree to our:
    </Typography>
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: { xs: 1, sm: 3 },
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
      }}
    >
      <Typography
        component="a"
        href="https://www.afterservice.ai/terms/"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: "#4169e1",
          textDecoration: "underline",
          fontSize: "0.8rem",
          fontWeight: 600,
          "&:hover": {
            color: "#1e3a8a",
            textDecoration: "underline",
          },
        }}
      >
        Terms & Conditions
      </Typography>
      <Typography
        component="a"
        href="https://www.afterservice.ai/privacy/"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: "#4169e1",
          textDecoration: "underline",
          fontSize: "0.8rem",
          fontWeight: 600,
          "&:hover": {
            color: "#1e3a8a",
            textDecoration: "underline",
          },
        }}
      >
        Privacy Policy
      </Typography>
    </Box>
  </Box>
);

// AfterService AI veteran-focused authenticator components
const authenticatorComponents = {
  Header() {
    return (
      <Box
        sx={{
          textAlign: "center",
          mb: 4,
          // v1.1: the hero sits directly on the ink page - no separate blue band.
          background: "transparent",
          color: "white",
          py: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.5,
          }}
        >
          <img
            src="/afterservice-logo-reversed.svg?v=20260608"
            alt="AfterService digital assistant"
            style={{
              height: "44px",
              width: "auto",
              maxWidth: "100%",
              objectFit: "contain",
              display: "block",
            }}
            onError={(e) => {
              console.warn("Login logo not found, using text fallback");
              e.target.style.display = "none";
              const fallback = document.getElementById("login-logo-fallback");
              if (fallback) {
                fallback.style.display = "block";
              }
            }}
          />
          {/* Phase 999.106 W1 (LP-02): logo wordmark is branding, not document
              structure. Demote from default <h3> to <span>. */}
          <Typography
            variant="h3"
            component="span"
            sx={{
              color: "white",
              fontWeight: 700,
              display: "none",
            }}
            id="login-logo-fallback"
          >
            AfterService
          </Typography>
        </Box>
        {/* Phase 999.106 W1 (LP-02): "digital assistant" promoted from default <h4>
            to <h1> - the page's primary topic. Visual variant="h4" preserved. */}
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontFamily: "var(--as-font-display)",
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "var(--as-d1)",
            mb: 1,
          }}
        >
          Digital assistant
        </Typography>
        {/* Phase 999.106 W1 (LP-02): "Empowering Veterans..." promoted from
            default <h6> to <h2> - the subheading. Visual variant="h6" preserved. */}
        <Typography
          variant="h6"
          component="h2"
          sx={{
            opacity: 0.9,
            color: "white",
            fontWeight: 400,
          }}
        >
          An assistant in every pocket
        </Typography>
        <Typography
          variant="body2"
          sx={{
            opacity: 0.8,
            color: "white",
            mt: 1,
            fontStyle: "italic",
          }}
        >
          Built by veterans, for veterans
        </Typography>
      </Box>
    );
  },

  SignIn: {
    Footer() {
      return (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Button
              variant="text"
              onClick={() => {
                if (window.openPasswordReset) {
                  window.openPasswordReset();
                } else {
                  console.error("❌ Password reset function not available");
                }
              }}
              sx={{
                color: "#5086ed !important",
                textDecoration: "underline",
                fontWeight: 500,
                fontSize: "0.9rem",
                "&:hover": {
                  backgroundColor: "rgba(80, 134, 237, 0.1)",
                  textDecoration: "underline",
                  color: "#5086ed !important",
                },
                "&:focus": {
                  color: "#5086ed !important",
                  backgroundColor: "rgba(80, 134, 237, 0.15)",
                },
              }}
            >
              Forgot your password?
            </Button>
          </Box>
          <LegalLinksComponent />
          <PublicCrisisStrip />
        </Box>
      );
    },
  },

  SignUp: {
    Footer() {
      return (
        <Box sx={{ mt: 2 }}>
          <LegalLinksComponent />
          <PublicCrisisStrip />
        </Box>
      );
    },
  },

  Footer() {
    return (
      <Box
        sx={{
          mt: 2,
          py: 2,
          textAlign: "center",
          background: "transparent",
          color: "var(--as-d2)",
        }}
      >
        <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
          Data & LLM is stored, processed, and remains onshore in Australia
        </Typography>
      </Box>
    );
  },
};


function App() {
  // Use configuration from context
  const { config } = useConfig();
  const themeConfig = useThemeConfig();
  const featureFlags = useFeatureFlags();

  // Core state
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [user, setUser] = useState(null);

  // Loading states
  const [authChecking, setAuthChecking] = useState(true);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);

  // App state
  const [appReady, setAppReady] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [notification, setNotification] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [showEarlyAccessAlert, setShowEarlyAccessAlert] = useState(false);
  const [showFeedbackAlert, setShowFeedbackAlert] = useState(false);

  // Password reset state
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  // Phase 999.72 Plan 11 (D-33 / D-34): Reconsent modal state.
  // acceptedConsentVersion: string | null - the version the user has on record.
  //   null   = legacy user (pre-999.71) - onboarding flow handles them, NO reprompt.
  //   '1.0'  = existing user who accepted v1.0 - reprompt when CURRENT bumps to '2.0'.
  //   '2.0'  = up-to-date - no prompt.
  const [reconsentOpen, setReconsentOpen] = useState(false);
  const [acceptedConsentVersion, setAcceptedConsentVersion] = useState(null);

  // Refs
  const initializationInProgress = useRef(false);
  // Phase 999.107: ensures the consent-gated chat-session bootstrap runs at
  // most once per session, whether it fires at initial load (returning,
  // already-consented user) or deferred until consent is recorded (new user
  // finishing onboarding / reconsent acceptance).
  const sessionsBootstrapped = useRef(false);
  const feedbackTimerRef = useRef(null);

  // REMOVED: Keep-alive mechanism (part of warmup complexity)

  // Password reset handler - make it globally accessible
  const handlePasswordReset = useCallback(() => {
    setShowPasswordReset(true);
  }, []);

  // Make the password reset function globally accessible
  useEffect(() => {
    window.openPasswordReset = handlePasswordReset;
    return () => {
      delete window.openPasswordReset;
    };
  }, [handlePasswordReset]);

  // Load user sessions (for sidebar display only - don't change current session)
  const loadSessions = useCallback(async () => {
    try {
      const response = await apiService.getChatSessions();

      // Session response received - no sensitive data logged

      const sessionList = response.sessions || response || [];

      // Sort sessions by last activity (most recent first)
      const sortedSessions = sessionList.sort(
        (a, b) =>
          new Date(b.lastActivity || b.updatedAt || b.timestamp) -
          new Date(a.lastActivity || a.updatedAt || a.timestamp),
      );

      // Update sessions list but preserve current session
      setSessions((prev) => {
        // If we already have sessions (including the new one), merge them intelligently
        if (prev.length > 0) {
          const existingIds = new Set(prev.map((s) => s.sessionId));
          const newSessions = sortedSessions.filter(
            (s) => !existingIds.has(s.sessionId),
          );
          const merged = [...prev, ...newSessions];
          return merged;
        }
        return sortedSessions;
      });
    } catch (error) {
      // Don't change anything if loading fails
    }
  }, []);

  // SIMPLIFIED: Direct session loading (no warmup complexity)
  const loadInitialSession = useCallback(async () => {
    try {
      // Load or create session (simple, direct)
      const sessionResult = await apiService.createChatSession(false);
      const sessionData = sessionResult.session || sessionResult;

      // Set the session as current
      const sessionUpdate = {
        sessionId: sessionData.sessionId,
        timestamp:
          sessionData.timestamp ||
          sessionData.createdAt ||
          new Date().toISOString(),
        lastActivity:
          sessionData.lastActivity ||
          sessionData.updatedAt ||
          new Date().toISOString(),
        messageCount: sessionData.messageCount || 0,
        status: sessionData.status || "active",
        title:
          sessionData.title ||
          `Chat ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
      };

      setSessions([sessionUpdate]);
      setCurrentSession(sessionUpdate);

      // Load all previous sessions for sidebar (background)
      loadSessions().catch(() => {});
    } catch (sessionError) {
      setSessions([]);
      setCurrentSession(null);
    }
  }, [loadSessions]);

  // Initial authentication check with WebSocket cleanup
  const checkInitialAuth = useCallback(async () => {
    try {
      // Import SimpleChatLogic dynamically to avoid circular dependencies
      const { default: simpleChatLogic } =
        await import("./services/SimpleChatLogic");

      // Clear any stale WebSocket connections from previous session
      simpleChatLogic.disconnect(true);

      // Clear stale sessions to ensure fresh start
      setSessions([]);
      setCurrentSession(null);

      // Add small delay to show loading
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Fetch complete user info with Cognito attributes
      const completeUser = await getCognitoUserWithAttributes();
      setUser(completeUser);
    } catch (error) {
      // User not authenticated - this is fine, login screen will show
    } finally {
      setAuthChecking(false);
    }
  }, []);

  // Check if user needs onboarding (uses existing API + localStorage flag)
  const checkOnboardingStatus = useCallback(async () => {
    if (!user) return;

    // 999.95 - Fast-path localStorage key MUST be scoped per-user. Previously
    // we read an unscoped `afterservice_onboarding_complete=true` flag, so a
    // brand-new sign-up on a browser where another veteran had previously
    // completed onboarding short-circuited to needsOnboarding=false and
    // landed the new user on /dashboard instead of /onboarding. The unscoped
    // legacy key is intentionally NOT honoured for any user - we cannot
    // attribute it to the current user, so the API is the only safe source
    // of truth. The scoped key is populated below after the API confirms
    // completion (and on every successful onboarding completion in
    // OnboardingFlow.handleComplete).
    const userId = user.userId || user.username || user.sub;
    const scopedKey = userId ? `afterservice_onboarding_complete_${userId}` : null;
    const scopedFlag = scopedKey ? localStorage.getItem(scopedKey) : null;
    if (scopedFlag === "true") {
      setNeedsOnboarding(false);
      // D-34 regression fix (2026-08-13): this fast path used to return
      // without touching acceptedConsentVersion, leaving it null. The
      // reconsent effect treats null as "legacy user - never re-prompt",
      // so any veteran whose device carried the scoped flag could never
      // see the reconsent modal after a consent version bump, and would
      // eventually hit the server-side consent gate with no way to
      // re-accept. The onboarding decision above stays instant; this
      // fetch exists solely to feed the reconsent comparison.
      try {
        const profile = await apiService.getUserProfile();
        setAcceptedConsentVersion(
          profile && profile.acceptedConsentVersion
            ? profile.acceptedConsentVersion
            : null
        );
      } catch (_) {
        setAcceptedConsentVersion(null);
      }
      return;
    }

    setCheckingOnboarding(true);

    try {
      const profile = await apiService.getUserProfile();

      // Check if user has completed the Phase 11 onboarding flow.
      //
      // PROD sweep 2026-08-01 P0-3: this used to read `onboardingComplete`
      // only, and GET /user never returned that field - the profile Lambda
      // silently dropped it on write, so it could never come back on read.
      // `isComplete` was therefore ALWAYS falsy, and every veteran arriving on
      // a new device or after a cache clear was pushed back through the whole
      // wizard. The server now returns `onboardingCompletedAt` (a timestamp,
      // derived from first consent for accounts that predate the field). The
      // legacy booleans stay as a fallback so a stale cached profile shape
      // cannot resurrect the replay.
      const isComplete = Boolean(
        profile &&
        (profile.onboardingCompletedAt ||
          profile.onboardingComplete ||
          profile.onboarding_complete)
      );

      if (isComplete) {
        setNeedsOnboarding(false);
        // Write scoped fast-path so subsequent sign-ins skip the API.
        if (scopedKey) {
          try { localStorage.setItem(scopedKey, "true"); } catch (_) { /* quota - ignore */ }
        }
      } else {
        setNeedsOnboarding(true);
      }

      // Phase 999.72 Plan 11 (D-33 / D-34): capture the recorded consent
      // version so the reconsent effect can compare it to CURRENT_CONSENT_VERSION.
      // Legacy users (null / missing field) are intentionally left null - their
      // flow is the existing onboarding path, not the reconsent modal.
      if (profile && profile.acceptedConsentVersion) {
        setAcceptedConsentVersion(profile.acceptedConsentVersion);
      } else {
        setAcceptedConsentVersion(null);
      }
    } catch (error) {
      // New user or error loading profile - show onboarding
      setNeedsOnboarding(true);
      setAcceptedConsentVersion(null);
    } finally {
      setCheckingOnboarding(false);
    }
  }, [user]);

  // Phase 999.107: consent-gated chat-session bootstrap. The /sessions API
  // (session_manager_v5 -> require_ai_consent) returns 403 consent_required
  // until the user has accepted the CURRENT consent version, so firing it
  // pre-consent produced repeated 403s and a redirect-to-/onboarding loop
  // (the [consent] suppression in apiService was a symptom patch). We only
  // load sessions once consent is current; runs at most once via the ref.
  const bootstrapSessions = useCallback(async () => {
    if (sessionsBootstrapped.current) {
      return;
    }
    sessionsBootstrapped.current = true;
    try {
      await loadInitialSession();
    } catch (error) {
      // Allow a later consent signal to retry the bootstrap.
      sessionsBootstrapped.current = false;
    }
  }, [loadInitialSession]);

  // SIMPLIFIED: Initialize app after authentication (no warmup complexity).
  // This now ONLY flips appReady so the Router mounts (the onboarding redirect
  // depends on it). The chat-session bootstrap is consent-gated and handled by
  // the dedicated effect below - never call /sessions before consent here.
  const initializeApp = useCallback(async () => {
    // Prevent multiple initializations
    if (initializationInProgress.current) {
      return;
    }

    initializationInProgress.current = true;
    setAppReady(true);
    initializationInProgress.current = false;
  }, []);

  // Session handlers
  const handleSessionSelect = (session) => {
    setCurrentSession(session);
  };

  const handleSessionCreate = (newSession) => {
    setSessions((prev) => [newSession, ...prev]);
    setCurrentSession(newSession);
  };

  const handleSessionUpdate = (updatedSession) => {
    // Handle null/undefined session (session clearing)
    if (!updatedSession) {
      setCurrentSession(null);
      return;
    }

    setSessions((prev) => {
      const existingIndex = prev.findIndex(
        (s) => s.sessionId === updatedSession.sessionId,
      );
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          ...updatedSession,
        };
        return updated;
      } else {
        return [updatedSession, ...prev];
      }
    });

    // Only update currentSession if it's the same session or no current session
    if (
      !currentSession ||
      currentSession.sessionId === updatedSession.sessionId
    ) {
      setCurrentSession(updatedSession);
    }
  };

  const handleSessionDelete = (sessionId) => {
    setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
    if (currentSession?.sessionId === sessionId) {
      setCurrentSession(null);
    }
  };

  const handleNotificationClose = () => {
    setNotification({ ...notification, open: false });
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Handle Early Access (Beta) Alert. Persist dismissal in localStorage keyed
  // to the notice version so "Understood" sticks across hard reloads / direct
  // navigations (previously only sessionStorage, which is cleared on reload,
  // so the modal re-appeared every load). Bump BETA_NOTICE_VERSION to re-show
  // the modal to everyone when the beta notice content changes.
  const handleEarlyAccessAlertClose = () => {
    setShowEarlyAccessAlert(false);
    try {
      localStorage.setItem(BETA_NOTICE_DISMISSED_KEY, "true");
    } catch (_) {
      /* quota / privacy mode - non-fatal */
    }
    // Keep the per-session marker for any same-session consumers.
    const timestamp = Date.now().toString();
    sessionStorage.setItem("afterservice_early_access_alert_shown", "true");
    sessionStorage.setItem(
      "afterservice_early_access_alert_timestamp",
      timestamp,
    );
  };

  // Early Access Alert state tracking
  useEffect(() => {
    // State change tracked - no verbose logging needed

    // Update debug object for console inspection
    if (process.env.NODE_ENV === "development") {
      window.APP_DEBUG = {
        appReady,
        user: !!user,
        showEarlyAccessAlert,
        showFeedbackAlert,
        authChecking,
      };
    }
  }, [showEarlyAccessAlert, appReady, user, showFeedbackAlert, authChecking]);

  // Handle Feedback Alert
  const handleFeedbackAlertClose = () => {
    setShowFeedbackAlert(false);
    // Remember that user has permanently dismissed the feedback alert
    localStorage.setItem("afterservice_feedback_alert_dismissed", "true");
    // Clear any running timer
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
  };

  const handleFeedbackAlertNotNow = () => {
    setShowFeedbackAlert(false);
    // Reset the 5-minute timer
    startFeedbackTimer();
  };

  const startFeedbackTimer = useCallback(() => {
    // Clear any existing timer
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
    }

    // Set new 5-minute timer
    feedbackTimerRef.current = setTimeout(
      () => {
        // Check if user hasn't permanently dismissed the alert
        const hasDismissed = localStorage.getItem(
          "afterservice_feedback_alert_dismissed",
        );
        if (!hasDismissed) {
          setShowFeedbackAlert(true);
        }
      },
      5 * 60 * 1000,
    ); // 5 minutes
  }, []);

  // Show Beta Alert after user logs in, unless the user has already dismissed
  // this notice version (persisted in localStorage). Dismissal now sticks
  // across hard reloads / direct navigations instead of re-prompting on every
  // load. 3-second delay lets the app finish loading first.
  useEffect(() => {
    if (appReady && user) {
      let dismissed = false;
      try {
        dismissed =
          localStorage.getItem(BETA_NOTICE_DISMISSED_KEY) === "true";
      } catch (_) {
        /* privacy mode - treat as not dismissed */
      }
      if (dismissed) {
        return;
      }
      const alertTimer = setTimeout(() => {
        setShowEarlyAccessAlert(true);
      }, 3000);

      return () => {
        clearTimeout(alertTimer);
      };
    }
  }, [appReady, user]);

  // Force show event listener for development
  useEffect(() => {
    const forceShowHandler = () => {
      setShowEarlyAccessAlert(true);
    };

    window.addEventListener("force-show-early-access", forceShowHandler);

    return () => {
      window.removeEventListener("force-show-early-access", forceShowHandler);
    };
  }, []);

  // Start Feedback Alert timer when app is ready
  useEffect(() => {
    if (appReady && user) {
      // Check if user has permanently dismissed the feedback alert
      const hasDismissed = localStorage.getItem(
        "afterservice_feedback_alert_dismissed",
      );

      if (!hasDismissed) {
        // Start the 5-minute timer
        startFeedbackTimer();
      }
    }

    // Cleanup timer on unmount
    return () => {
      if (feedbackTimerRef.current) {
        clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = null;
      }
    };
  }, [appReady, user, startFeedbackTimer]);

  // Initial authentication check
  useEffect(() => {
    checkInitialAuth();
  }, [checkInitialAuth]);

  // Check onboarding status when user is authenticated
  useEffect(() => {
    if (user && !checkingOnboarding) {
      checkOnboardingStatus();
    }
  }, [user, checkOnboardingStatus]);

  // Phase 999.72 Plan 11 (D-34): Reconsent trigger.
  // When the user has a recorded acceptedConsentVersion that differs from
  // CURRENT_CONSENT_VERSION, open the reconsent modal. Legacy users (null)
  // are handled by the onboarding flow and MUST NOT see this modal.
  useEffect(() => {
    if (!user) return;
    if (needsOnboarding) return; // onboarding flow captures new consent
    if (!acceptedConsentVersion) return; // legacy user - do not re-prompt
    if (acceptedConsentVersion !== CURRENT_CONSENT_VERSION) {
      setReconsentOpen(true);
    }
  }, [user, needsOnboarding, acceptedConsentVersion]);

  // Phase 999.72 Plan 11 (D-34): Reaccept handler.
  // Writes aiDocumentConsent=true plus the modal's actual rendered copy via
  // PUT /user, so the backend's consent audit record hashes the real text
  // shown (Privacy Policy 7.1) - previously this sent the literal 'v1.0'.
  // Plan 06 backend: user_profile_service_v3 persists the new
  // acceptedConsentVersion on this PUT and audit-log writes the event.
  const handleReaccept = useCallback(async () => {
    try {
      await apiService.updateUserProfile({
        aiDocumentConsent: true,
        aiDocumentConsentText: reconsentTextFor(
          CURRENT_CONSENT_VERSION,
          acceptedConsentVersion,
        ),
        aiDocumentConsentDate: new Date().toISOString(),
      });
      setAcceptedConsentVersion(CURRENT_CONSENT_VERSION);
    } catch (err) {
      // Non-blocking - surface via Snackbar but keep user in the app.
      console.error("❌ Reconsent PUT /user failed:", err);
      setNotification({
        open: true,
        message:
          "We couldn't record your acceptance right now. Please try again.",
        severity: "warning",
      });
    } finally {
      setReconsentOpen(false);
    }
  }, []);

  // Initialize app once the onboarding status check has completed. We no longer
  // gate on `!needsOnboarding` - onboarding lives inside the Router, and if we
  // don't flip appReady=true the Router never mounts, so the `<Navigate to=
  // "/onboarding">` redirect for new users never fires and they're stuck on
  // the "Preparing your digital assistant…" fallback forever.
  //
  // initializeApp() only loads chat sessions (cheap, non-blocking) and is safe
  // to run whether or not the user still needs onboarding.
  useEffect(() => {
    if (
      user &&
      !checkingOnboarding &&
      !appReady &&
      !initializationInProgress.current
    ) {
      initializeApp();
    }
  }, [user, checkingOnboarding, appReady, initializeApp]);

  // Phase 999.107: consent-gated chat-session bootstrap trigger. Once the app
  // is mounted AND the user has accepted the CURRENT consent version, load the
  // chat sessions. This fires immediately for returning consented users and
  // is deferred (no page refresh needed) for new users the moment onboarding
  // completion / reconsent acceptance flips acceptedConsentVersion to current.
  useEffect(() => {
    if (appReady && acceptedConsentVersion === CURRENT_CONSENT_VERSION) {
      bootstrapSessions();
    }
  }, [appReady, acceptedConsentVersion, bootstrapSessions]);

  // Create theme based on configuration
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: "light",
          primary: {
            main: themeConfig.primary || "#5086ed",
            light: "#7ba4f0",
            dark: "#3b67c9",
            contrastText: "#ffffff",
          },
          secondary: {
            main: themeConfig.secondary || "#FFB300",
            light: "#FFC107",
            dark: "#FF8F00",
            contrastText: "#000000",
          },
          background: {
            // v1.1: cleaner near-white canvas (was #F8F9FA). Reads
            // infrastructure-grade rather than consumer; cards (white) lift
            // off it via a hairline border instead of shadow.
            default: themeConfig.background || "#FAF9F5",
            paper: "#ffffff",
          },
          text: {
            primary: themeConfig.textPrimary || "#1A1A1A",
            secondary: themeConfig.textSecondary || "#666666",
          },
          info: {
            main: "#0277BD",
            light: "#03A9F4",
            dark: "#01579B",
          },
        },
        typography: {
          fontFamily:
            themeConfig.fontFamily ||
            "Inter, system-ui, -apple-system, sans-serif",
          // v1.1: titles are ink (not blanket brand-blue); accent is reserved for
          // a single highlighted word per the mockups. Display face = Space Grotesk
          // (loaded in public/index.html), falling back to Inter.
          h1: { fontFamily: '"Space Grotesk", Inter, system-ui, sans-serif', fontWeight: 700, letterSpacing: '-0.03em' },
          h2: { fontFamily: '"Space Grotesk", Inter, system-ui, sans-serif', fontWeight: 600, letterSpacing: '-0.02em' },
          h3: { fontFamily: '"Space Grotesk", Inter, system-ui, sans-serif', fontWeight: 600, letterSpacing: '-0.02em' },
          h4: { fontFamily: '"Space Grotesk", Inter, system-ui, sans-serif', fontWeight: 600, fontSize: '2.125rem', letterSpacing: '-0.02em' },
          h5: { fontWeight: 600 },
          h6: { fontWeight: 600 },
          body1: {
            lineHeight: 1.6,
          },
        },
        shape: {
          borderRadius: 8,
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              // P3 #23: single scroll root - the document (html) owns vertical
              // scrolling. Never set overflow hidden on html/body globally, or
              // window.scrollTo and anchor navigation become no-ops. The chat
              // route's inner scroller is the one exception (see AppLayout).
              html: {
                overflowY: "auto",
                overflowX: "hidden",
              },
              // v1.1: figures align everywhere (stats, tables, %, dates).
              body: {
                fontVariantNumeric: "tabular-nums",
              },
            },
          },
          MuiButtonBase: {
            styleOverrides: {
              root: {
                // Keyboard focus ring on every ButtonBase descendant (Button,
                // IconButton, Tab, Checkbox, Radio, MenuItem, Chip). Outline - 
                // not box-shadow - so it survives the MuiButton
                // boxShadow:'none' override below and still renders in
                // Windows High Contrast / forced-colors mode.
                "&.Mui-focusVisible": {
                  outline: "3px solid var(--as-blue-600)",
                  outlineOffset: "2px",
                },
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 600,
                // v1.1: slightly tighter, more architectural radius (was 12).
                borderRadius: 8,
                padding: "8px 20px",
                boxShadow: "none",
                // v1.1: restraint - no shadow-pop / no lift on hover. Hover is a
                // quiet background change handled by MUI's default state layer.
                "&:hover": {
                  boxShadow: "none",
                },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                fontWeight: 600,
                // v1.1: pill badges read as soft tint + word.
                borderRadius: 999,
                // v1.1: all figures use tabular numerals so columns/metrics align.
                fontVariantNumeric: "tabular-nums",
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                // v1.1: depth from BORDER, not shadow. Resting cards are flat
                // with a hairline; only a subtle lift appears on hover.
                borderRadius: 12,
                border: "1px solid #E7E3DA",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "0 1px 2px rgba(16, 24, 32, 0.04)",
                },
              },
            },
          },
          MuiAvatar: {
            styleOverrides: {
              root: {
                // v1.1: avatars are flat (no brand glow); identity comes from
                // the mark, not a halo.
                boxShadow: "none",
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                fontSize: '0.875rem',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-notchedOutline legend': {
                  fontSize: '0.65em',
                },
              },
              input: {
                padding: '14px',
                '&:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px #fff inset',
                },
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              variant: 'outlined',
              size: 'medium',
            },
          },
        },
      }),
    [themeConfig],
  );

  // Early exit for embed mode - no Authenticator wrapper for embed routes
  if (window.location.pathname.startsWith("/embed")) {
    if (!window.location.pathname.startsWith("/embed-advocate")) {
      window.location.replace("/embed-advocate" + window.location.search);
      return null;
    }
    return (
      <React.Suspense fallback={null}>
        <AdvocateChatEmbed />
      </React.Suspense>
    );
  }

  // Phase 999.100 W4 (D-16 + W4-D-07): /about-ai is public + unauthenticated.
  // Same early-exit pattern as /embed - bypass the Authenticator wrapper so a
  // visitor without an account can read the regulatory-position statement.
  //
  // 2026-05-10: /legal/privacy stub removed - privacy policy consolidated to
  // single canonical at https://afterservice.ai/privacy. Any deep-link to the
  // old in-app path 302-redirects to the canonical (handled below).
  const publicPath = window.location.pathname.replace(/\/$/, "");
  if (publicPath === "/legal/privacy") {
    // Permanent redirect to canonical marketing-site privacy URL
    window.location.replace("https://www.afterservice.ai/privacy/");
    return null;
  }
  // Drop 2 (C-01, C-02): /crisis is public whenever nobody is signed in,
  // including while auth is still being checked, so the crisis page never
  // sits behind sign-in. Keyed on `user` as well as the path: a signed-in
  // user stays in the authenticated tree on every re-render.
  const publicCrisis = publicPath === "/crisis" && !user;
  if (publicPath === "/about-ai" || publicCrisis) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PublicRouter />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <Box
          sx={{
            // v1.1: full-screen chrome (auth + loading) uses the ink command-bar
            // colour, not brand blue - ties the unauthenticated experience to the
            // dark header. Authenticated app keeps the light canvas.
            backgroundColor: user ? '#f9fafb' : 'var(--as-ink)',
            minHeight: "100vh",
            transition: 'none',
            display: "flex",
            alignItems: user ? "flex-start" : "center",
            justifyContent: user ? "flex-start" : "center",
            "& .amplify-authenticator": {
              background: "transparent !important",
            },
            "& .amplify-tabs": {
              background: "transparent !important",
            },
            "& .amplify-authenticator__modal": {
              background: "transparent !important",
            },
            ...(user && {
              "& .amplify-authenticator": {
                background: "transparent !important",
              },
              "& .amplify-tabs": {
                background: "transparent !important",
              },
            }),
          }}
        >
          {/* Initial loading screen (during auth check) */}
          {authChecking && (
            <Box textAlign="center" sx={{ maxWidth: 400, px: 3 }}>
              <Box
                sx={{
                  mb: 4,
                  height: "120px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src="/afterservice-logo-reversed.svg?v=20260608"
                  alt="AfterService digital assistant"
                  style={{
                    height: "84px",
                    width: "auto",
                    maxWidth: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                  onError={(e) => {
                    console.warn("Loading logo not found, using text fallback");
                    e.target.style.display = "none";
                    const fallback = document.getElementById(
                      "loading-logo-fallback",
                    );
                    if (fallback) {
                      fallback.style.display = "block";
                    }
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    display: "none",
                  }}
                  id="loading-logo-fallback"
                >
                  AfterService
                </Typography>
              </Box>
              <CircularProgress
                size={48}
                sx={{
                  mb: 3,
                  color: "white",
                }}
              />
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: "white",
                }}
              >
                AfterService digital assistant
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 2,
                  color: "white",
                  opacity: 0.9,
                }}
              >
                Loading your veteran support system...
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.8,
                  fontStyle: "italic",
                  color: "white",
                }}
              >
                Built by veterans, for veterans
              </Typography>
              <Box sx={{ mt: 3 }}>
                <PublicCrisisStrip tone="dark" />
              </Box>
            </Box>
          )}

          {/* Authentication and main app */}
          {!authChecking && (
            <>
              {/* Show password reset flow if requested */}
              {showPasswordReset && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="100vh"
                  sx={{ background: "var(--as-ink)" }}
                >
                  <PasswordResetFlow
                    onBackToSignIn={() => {
                      setShowPasswordReset(false);
                    }}
                    onSuccess={() => {
                      setShowPasswordReset(false);
                      setNotification({
                        open: true,
                        message:
                          "Password reset successful! You can now sign in with your new password.",
                        severity: "success",
                      });
                    }}
                  />
                </Box>
              )}

              {/* Add password requirements enhancer outside Authenticator */}
              <React.Suspense fallback={null}>
                <AmplifyPasswordEnhancer />
              </React.Suspense>

              {/* Only render Authenticator when NOT in password reset mode */}
              {!showPasswordReset && (
                <Authenticator
                  formFields={formFields}
                  components={authenticatorComponents}
                  loginMechanisms={["email"]}
                  signUpAttributes={["email"]}
                >
                  {({ signOut, user: authUser }) => {
                    // Authenticator render - minimal logging
                    if (process.env.NODE_ENV === "development") {
                      console.log(
                        "🔄 Auth State:",
                        appReady ? "Ready" : "Loading",
                      );
                    }

                    // Handle fresh login - fetch complete user attributes
                    if (authUser && !user) {
                      getCognitoUserWithAttributes()
                        .then((completeUser) => {
                          setUser(completeUser);
                        })
                        .catch((error) => {
                          console.error(
                            "❌ Error fetching user attributes, using basic info:",
                            error,
                          );
                          // Fallback to basic info if attribute fetch fails
                          const basicUserInfo = {
                            userId: authUser.userId || authUser.username,
                            username: authUser.username,
                            email:
                              authUser.signInDetails?.loginId ||
                              authUser.attributes?.email ||
                              "N/A",
                          };
                          setUser(basicUserInfo);
                        });
                    }

                    // Show login screen if not authenticated
                    if (!authUser) {
                      return null;
                    }

                    // REMOVED: Warmup screen (simplified approach)

                    // Show onboarding if needed
                    // needsOnboarding redirect is handled inside the Router below

                    // Show loading while checking onboarding
                    if (checkingOnboarding) {
                      return (
                        <Box
                          sx={{
                            position: "fixed",
                            inset: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            bgcolor: "#5086ed",
                          }}
                        >
                          <Box textAlign="center">
                            <CircularProgress
                              size={48}
                              sx={{ color: "white", mb: 2 }}
                            />
                            <Typography variant="body1" sx={{ color: "white" }}>
                              Setting up your profile...
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }

                    // Main app interface
                    if (appReady) {
                      return (
                        <AdvocateWidgetProvider>
                        <Router>
                          <ScrollToTop />
                          <Routes>
                            <Route
                              path="/"
                              element={
                                needsOnboarding && !checkingOnboarding
                                  ? <Navigate to="/onboarding" replace />
                                  : (
                                    <AppLayout user={user} sessions={sessions}>
                                      <React.Suspense
                                        fallback={
                                          <LoadingFallback variant="minimal" />
                                        }
                                      >
                                        <DashboardPage user={user} />
                                      </React.Suspense>
                                    </AppLayout>
                                  )
                              }
                            />
                            <Route
                              path="/dashboard"
                              element={
                                needsOnboarding && !checkingOnboarding
                                  ? <Navigate to="/onboarding" replace />
                                  : (
                                    <AppLayout user={user} sessions={sessions}>
                                      <React.Suspense fallback={<LoadingFallback variant="minimal" />}>
                                        <DashboardPage user={user} />
                                      </React.Suspense>
                                    </AppLayout>
                                  )
                              }
                            />
                            <Route
                              path="/chat"
                              element={<Navigate to="/assistant" replace />}
                            />
                            <Route
                              path="/profile"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback variant="profile" />
                                    }
                                  >
                                    <UserProfile user={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            {/* /connections route retired in Phase 999.85 (2026-04-21) - 
                                the legacy P2P ConnectionCenter's backend
                                (`afterservice-connection-service` on tx31htvsfk) receives
                                0 CloudWatch invocations and its four endpoints return 404.
                                Veteran-org connection management now lives at /organisations
                                (MyOrgsPage). */}
                            <Route
                              path="/tools"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading tools..."
                                      />
                                    }
                                  >
                                    <ToolsCenter cognitoUser={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/directory"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading directory..."
                                      />
                                    }
                                  >
                                    <ServiceDirectory cognitoUser={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/find-support"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading support directory..."
                                      />
                                    }
                                  >
                                    <FindSupportPage cognitoUser={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route path="/eso-directory" element={<Navigate to="/find-support" replace />} />
                            <Route path="/esos" element={<Navigate to="/find-support" replace />} />
                            <Route path="/my-organisations" element={<Navigate to="/my-orgs" replace />} />
                            <Route
                              path="/my-orgs"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading your organisations..."
                                      />
                                    }
                                  >
                                    <MyOrgsPage cognitoUser={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/eligibility"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading eligibility checker..."
                                      />
                                    }
                                  >
                                    {/* C-27, 5 Oct 2026: holding card until the checker is rebuilt. */}
                                    <EligibilityHolding />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/entitlements"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading entitlements..."
                                      />
                                    }
                                  >
                                    <EntitlementsPage cognitoUser={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/claims"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading claims..."
                                      />
                                    }
                                  >
                                    <ClaimsPage cognitoUser={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/mental-health"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading mental health support..."
                                      />
                                    }
                                  >
                                    <MentalHealthPage cognitoUser={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/appeals"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading appeals..."
                                      />
                                    }
                                  >
                                    <AppealsPage cognitoUser={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/register-provider"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading registration..."
                                      />
                                    }
                                  >
                                    <ProviderRegistration />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/providers"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading directory..."
                                      />
                                    }
                                  >
                                    <ProviderDirectory cognitoUser={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/insights"
                              element={
                                user?.groups?.includes('admin') || user?.role === 'admin' ? (
                                  <AppLayout user={user} sessions={sessions}>
                                    <React.Suspense
                                      fallback={
                                        <LoadingFallback
                                          variant="minimal"
                                          message="Loading insights..."
                                        />
                                      }
                                    >
                                      <InsightsDashboard cognitoUser={user} />
                                    </React.Suspense>
                                  </AppLayout>
                                ) : (
                                  <Navigate to="/dashboard" replace />
                                )
                              }
                            />
                            <Route
                              path="/documents"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading documents..."
                                      />
                                    }
                                  >
                                    <DocumentVaultPage cognitoUser={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/notifications"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading notifications..."
                                      />
                                    }
                                  >
                                    <NotificationsPage />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/events"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading events calendar..."
                                      />
                                    }
                                  >
                                    <EventsCalendarPage user={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/transition"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading transition timeline..."
                                      />
                                    }
                                  >
                                    <TransitionTimelinePage cognitoUser={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/assistant"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense fallback={<LoadingFallback variant="minimal" />}>
                                    {/* Phase 999.72 Plan 02 (D-05): AIConsentGate blocks /assistant
                                        unless the user has completed onboarding AND given AI consent.
                                        Doc Vault, Claims, Timeline, Profile etc. remain un-gated. */}
                                    <AIConsentGate feature="the digital assistant" user={user}>
                                      <AdvocateChatPage user={user} />
                                    </AIConsentGate>
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            {/* Legacy route: /advocate renamed to /assistant (AI product
                                rebrand). Redirect preserves old bookmarks and query params. */}
                            <Route
                              path="/advocate"
                              element={<Navigate to={`/assistant${window.location.search}`} replace />}
                            />
                            <Route
                              path="/onboarding"
                              element={
                                <React.Suspense
                                  fallback={
                                    <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'var(--as-ink)' }} />
                                  }
                                >
                                  <OnboardingFlow
                                    cognitoUser={user}
                                    onComplete={() => {
                                      setNeedsOnboarding(false);
                                      // Completing onboarding records acceptance
                                      // of the current consent version (the flow
                                      // PUTs aiDocumentConsent). Reflect it in
                                      // state so the consent-gated session
                                      // bootstrap fires without a page refresh.
                                      setAcceptedConsentVersion(
                                        CURRENT_CONSENT_VERSION,
                                      );
                                    }}
                                  />
                                </React.Suspense>
                              }
                            />
                            <Route
                              path="/crisis"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense fallback={<LoadingFallback variant="minimal" message="Loading..." />}>
                                    <CrisisPage />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/help"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense fallback={<LoadingFallback variant="minimal" />}>
                                    <HelpPage user={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="/settings"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense fallback={<LoadingFallback variant="minimal" />}>
                                    <SettingsPage user={user} />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                            <Route
                              path="*"
                              element={
                                <AppLayout user={user} sessions={sessions}>
                                  <React.Suspense fallback={<LoadingFallback variant="minimal" />}>
                                    <NotFoundPage />
                                  </React.Suspense>
                                </AppLayout>
                              }
                            />
                          </Routes>
                          {/* Persistent floating AI widget - hidden on /assistant (page IS the chat) */}
                          <WidgetGuard>
                            <React.Suspense fallback={null}>
                              <AIChatWidget />
                            </React.Suspense>
                          </WidgetGuard>
                          {/* Phase 999.72 Plan 11 (D-34): Reconsent modal overlay.
                              Renders only when the user has a stored acceptedConsentVersion
                              that differs from CURRENT_CONSENT_VERSION (see reconsent
                              trigger useEffect above). Legacy users (null version) are
                              routed through the onboarding flow instead. */}
                          {reconsentOpen && (
                            <ReconsentPrompt
                              open={reconsentOpen}
                              onAccept={handleReaccept}
                              onSignOut={signOut}
                              currentVersion={CURRENT_CONSENT_VERSION}
                              acceptedVersion={acceptedConsentVersion}
                            />
                          )}
                          {/* Beta welcome + feedback modals (restored after the
                              /chat → /advocate refactor in 2ea0831e wiped the
                              JSX render). State, handlers, and timers all
                              live in the App() component scope above. */}
                          <React.Suspense fallback={null}>
                            <BetaNotice
                              open={showEarlyAccessAlert}
                              onClose={handleEarlyAccessAlertClose}
                              onOpenFeedback={() => setShowFeedbackAlert(true)}
                            />
                            <FeedbackNotice
                              open={showFeedbackAlert}
                              onClose={handleFeedbackAlertClose}
                              onNotNow={handleFeedbackAlertNotNow}
                            />
                          </React.Suspense>
                        </Router>
                        </AdvocateWidgetProvider>
                      );
                    }

                    // Fallback loading state
                    return (
                      <Box
                        sx={{
                          position: "fixed",
                          inset: 0,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          background: "var(--as-ink)",
                          zIndex: 9999,
                        }}
                      >
                        <Box
                          component="img"
                          src="/afterservice-logo-reversed.svg?v=20260608"
                          alt="AfterService"
                          sx={{ height: 56, width: "auto", mb: 3 }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                        <CircularProgress
                          size={48}
                          sx={{ color: "white", mb: 2 }}
                        />
                        <Typography variant="h6" sx={{ color: "white", fontWeight: 700, mb: 0.5 }}>
                          AfterService digital assistant
                        </Typography>
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                          Preparing your digital assistant...
                        </Typography>
                        <Box sx={{ mt: 3, px: 2 }}>
                          <PublicCrisisStrip tone="dark" />
                        </Box>
                      </Box>
                    );
                  }}
                </Authenticator>
              )}
            </>
          )}
        </Box>

        <Snackbar
          open={notification?.open}
          autoHideDuration={6000}
          onClose={handleNotificationClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleNotificationClose}
            severity={notification?.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {notification?.message}
          </Alert>
        </Snackbar>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
